import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Avatar from '@material-ui/core/Avatar';
import OrganisationLayoutContainer from '../../Components/Layout/LayoutContainer';
import AuthMenu from '../../Components/Organisations/Shared/Menu';
import OrganisationMembershipActions from '../../Redux/OrganisationMembershipRedux';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2, 3, 3),
    marginBottom: theme.spacing(2),
  },
  orgaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  orgaInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(2),
    width: 40,
    height: 40,
  },
}));

type Props = {
  family: Object,
  organisation: Object,
};

const Notifications = ({ family, organisation }: Props) => {
  const classes = useStyles();
  const currentUser = useSelector(state => state.auth.currentUser);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [optimisticAlerts, setOptimisticAlerts] = React.useState({});
  const [pendingOrgas, setPendingOrgas] = React.useState({});

  const organisations = (currentUser?.organisations || []).filter(
    orga => orga.type !== 'Organisations::Family',
  );

  React.useEffect(() => {
    organisations.forEach(orga => {
      if (pendingOrgas[orga.id]) {
        const expected = optimisticAlerts[orga.id];
        const current = orga.email_alerts !== false;
        if (expected === current) {
          setPendingOrgas(prev => ({ ...prev, [orga.id]: false }));
        }
      }
    });
  }, [currentUser]);

  const handleToggle = (orga, checked) => {
    if (!currentUser) return;

    setOptimisticAlerts(prev => ({ ...prev, [orga.id]: checked }));
    setPendingOrgas(prev => ({ ...prev, [orga.id]: true }));
    dispatch(
      OrganisationMembershipActions.updateOrganisationMembershipRequest(
        orga.holding_slug,
        orga.slug,
        currentUser.id,
        { email_alerts: checked },
        currentUser.id,
        'notif',
      ),
    );
  };

  return (
    <OrganisationLayoutContainer menu={<AuthMenu organisation={organisation} family={family} />}>
      <Paper className={classes.paper}>
        <Typography align="center" component="h1" variant="h5" gutterBottom>
          {t('APP.USER.SETTINGS.NOTIFICATIONS.TITLE')}
        </Typography>
        <Typography align="center" variant="body2" color="textSecondary" gutterBottom>
          {t('APP.USER.SETTINGS.NOTIFICATIONS.SUBTITLE')}
        </Typography>
        {organisations.map(orga => {
          const isChecked =
            orga.id in optimisticAlerts ? optimisticAlerts[orga.id] : orga.email_alerts !== false;
          return (
            <div key={orga.id} className={classes.orgaRow}>
              <div className={classes.orgaInfo}>
                <Avatar src={orga.avatar_url} alt={orga.name} className={classes.avatar} />
                <Typography variant="body1">{orga.name}</Typography>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={isChecked}
                    onChange={event => handleToggle(orga, event.target.checked)}
                    name={`emailAlerts-${orga.id}`}
                    color="primary"
                    disabled={!!pendingOrgas[orga.id]}
                  />
                }
                label=""
              />
            </div>
          );
        })}
      </Paper>
    </OrganisationLayoutContainer>
  );
};

export default Notifications;
